import { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { durations } from '../../../theme';
import { useReducedMotion } from '../../../hooks/useReducedMotion';
import { photoPositions, type DeckItem, type Slide } from './deck';
import {
  advanceCrossfade,
  clearPrevious,
  initialCrossfade,
  neighborImageUrls,
  targetImageUrls,
  type CrossfadeState,
} from './crossfade';
import { injectSlideshowMotionCss } from './slideshowMotionCss';
import { SlideStage } from './SlideStage';

// A slide never fades in later than this after becoming current, loaded or
// not — a presenter live on a call should never feel like the deck stalled.
const MAX_WAIT_MS = 600;

/** Raw-image preload, distinct from expo-image's own cache (SlideStage's
 *  `loadedUrls`, useSlideshowController's Image.prefetch): this one exists
 *  only to know, in plain JS, the instant a URL is decodable, so the
 *  crossfade never reveals a half-loaded photo. Module-level so a URL
 *  warmed once this session never re-waits. */
const warmedUrls = new Set<string>();

function preload(url: string, onDone: () => void): void {
  if (warmedUrls.has(url)) {
    onDone();
    return;
  }
  if (typeof Image === 'undefined') {
    onDone();
    return;
  }
  const img = new Image();
  img.onload = img.onerror = () => {
    warmedUrls.add(url);
    onDone();
  };
  img.src = url;
}

/**
 * The fullscreen presentation's two-layer crossfade: `current` (the slide
 * fading in) painted over `previous` (the slide it's replacing, held at full
 * opacity underneath so there's never a gap of empty stage). See
 * crossfade.ts for the pure state transitions and slideshowMotionCss.ts for
 * the CSS this drives.
 *
 * A slide only starts its fade-in once its own image(s) have loaded (raw
 * `Image()` preload, not expo-image's) or MAX_WAIT_MS has passed — never
 * later. Keyboard nav (useSlideshowKeys) moves `index` synchronously and
 * independently of any of this, so two rapid Next presses still land two
 * slides forward with nothing to wait on; a layer mid-fade when superseded
 * simply becomes the new `previous`, never a stuck third layer.
 */
export function PresentationCrossfade({
  deck,
  index,
  onPrev,
  onNext,
  onAddressPress,
}: {
  deck: DeckItem[];
  index: number;
  onPrev: () => void;
  onNext: () => void;
  onAddressPress: (slide: Slide) => void;
}) {
  const reducedMotion = useReducedMotion();
  const { numberByIndex, total } = useMemo(() => photoPositions(deck), [deck]);
  const item = deck[index];

  useEffect(() => {
    injectSlideshowMotionCss();
  }, []);

  const [state, setState] = useState<CrossfadeState | undefined>(() =>
    item ? initialCrossfade({ item, index }) : undefined
  );
  const [ready, setReady] = useState(true);
  const readyRef = useRef(true);
  const waitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Advance the crossfade whenever the deck hands us a different item.
  useEffect(() => {
    setState((prev) => {
      if (!item) return undefined;
      if (!prev) return initialCrossfade({ item, index });
      return advanceCrossfade(prev, { item, index });
    });
  }, [item, index]);

  // Gate readiness on the new current layer's images (or the reduced-motion
  // / no-image fast path), and warm its neighbours regardless.
  const currentItem = state?.current.item;
  useEffect(() => {
    clearTimeout(waitTimer.current);
    clearTimeout(settleTimer.current);
    if (!currentItem) return;

    const urls = targetImageUrls(currentItem);
    if (reducedMotion || urls.length === 0) {
      readyRef.current = true;
      setReady(true);
    } else {
      readyRef.current = false;
      setReady(false);
      let remaining = urls.length;
      urls.forEach((url) =>
        preload(url, () => {
          remaining -= 1;
          if (remaining <= 0 && !readyRef.current) {
            readyRef.current = true;
            setReady(true);
          }
        })
      );
      waitTimer.current = setTimeout(() => {
        if (!readyRef.current) {
          readyRef.current = true;
          setReady(true);
        }
      }, MAX_WAIT_MS);
    }

    neighborImageUrls(deck, index).forEach((url) => preload(url, () => {}));

    return () => clearTimeout(waitTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem, reducedMotion]);

  // Once the current layer is ready to show, drop `previous` after the
  // crossfade settles (instantly under reduced motion).
  useEffect(() => {
    if (!ready || !state?.previous) return;
    settleTimer.current = setTimeout(
      () => setState((prev) => (prev ? clearPrevious(prev) : prev)),
      reducedMotion ? 0 : durations.slow
    );
    return () => clearTimeout(settleTimer.current);
  }, [ready, state, reducedMotion]);

  if (!state) {
    return (
      <SlideStage
        layout="fullscreen"
        item={undefined}
        onPrev={onPrev}
        onNext={onNext}
        onAddressPress={onAddressPress}
      />
    );
  }

  const counterFor = (i: number) => {
    const n = numberByIndex.get(i);
    return n ? `${n} of ${total}` : undefined;
  };

  return (
    <View style={styles.root}>
      {state.previous ? (
        <View style={styles.layer} pointerEvents="none">
          <SlideStage
            layout="fullscreen"
            item={state.previous.item}
            counterText={counterFor(state.previous.index)}
            onPrev={onPrev}
            onNext={onNext}
            onAddressPress={onAddressPress}
            variant="previous"
            disableItemFade
          />
        </View>
      ) : null}
      <View
        key={index}
        style={[styles.layer, { opacity: ready ? 1 : 0 }]}
        // @ts-expect-error rediAnim is a web-only DOM dataset hook picked up
        // by slideshowMotionCss.ts's opacity transition; ignored on native.
        dataSet={{ rediAnim: 'slideshow-fade' }}>
        <SlideStage
          layout="fullscreen"
          item={state.current.item}
          counterText={counterFor(state.current.index)}
          onPrev={onPrev}
          onNext={onNext}
          onAddressPress={onAddressPress}
          disableItemFade
          zoom={!reducedMotion}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: '100%',
    position: 'relative',
  },
  layer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});
